import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import FeatherIcon from 'feather-icons-react';
import type { MappedSkill } from '../services/skillMapService';
import { skillDisplayName, traceRoleLabel, traceSkill, type TraceNode } from './skillScenario';

// AXISREF499: extends the body-part card to show how one urgency skill relates to the others
// and the priority it can lead to. Clicking a related skill follows it and adds to the chain.

interface SkillTraceProps {
  skills: MappedSkill[];
  region: string;
  regionName: string;
  chain: MappedSkill[];
  onFollow: (skill: MappedSkill) => void;
  onBackTo: (index: number) => void;
  onClose: () => void;
}

interface Curve {
  key: string;
  d: string;
  active: boolean;
}

export const SkillTrace: React.FC<SkillTraceProps> = ({ skills, region, regionName, chain, onFollow, onBackTo, onClose }) => {
  const current = chain[chain.length - 1];
  const trace = useMemo(() => traceSkill(skills, region, current), [skills, region, current]);
  const [openId, setOpenId] = useState<string | null>(null);
  const flowRef = useRef<HTMLDivElement>(null);
  const [curves, setCurves] = useState<Curve[]>([]);
  const currentId = `skill:${current.kind}:${current.id}`;
  const currentName = skillDisplayName(current);

  const nodeEl = (id: string) =>
    [...(flowRef.current?.querySelectorAll<HTMLElement>('[data-node]') ?? [])].find(el => el.dataset.node === id);

  // Draw the links from the rendered node positions so the flow reflows with the card. In the
  // stacked (narrow) layout the links are hidden by CSS and the steps read top to bottom.
  useLayoutEffect(() => {
    const flow = flowRef.current;
    if (!flow) return;
    const draw = () => {
      const box = flow.getBoundingClientRect();
      setCurves(
        trace.links.flatMap(([from, to]) => {
          const a = nodeEl(from)?.getBoundingClientRect();
          const b = nodeEl(to)?.getBoundingClientRect();
          if (!a || !b || b.left <= a.right) return [];
          const x1 = a.right - box.left;
          const y1 = a.top + a.height / 2 - box.top;
          const x2 = b.left - box.left;
          const y2 = b.top + b.height / 2 - box.top;
          const mid = (x1 + x2) / 2;
          return [{
            key: `${from}>${to}`,
            d: `M${x1} ${y1} C${mid} ${y1} ${mid} ${y2} ${x2} ${y2}`,
            active: from === currentId || to === currentId,
          }];
        }),
      );
    };
    draw();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(draw);
    observer.observe(flow);
    return () => observer.disconnect();
  }, [trace, currentId]);

  // Following a skill unmounts the button that was clicked; keep focus on the skill now being
  // traced so keyboard users stay in place. The first open leaves focus on the pill.
  const followed = useRef(false);
  useEffect(() => {
    setOpenId(null);
    if (!followed.current) {
      followed.current = true;
      return;
    }
    nodeEl(currentId)?.focus({ preventScroll: true, focusVisible: false } as FocusOptions);
  }, [currentId]);

  // When the trace stacks above the card details (narrower screens), bring it into view on open.
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!window.matchMedia?.('(max-width: 1199px)')?.matches) return;
    const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    sectionRef.current?.scrollIntoView?.({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
  }, []);

  const openNode = trace.columns.flatMap(c => c.nodes).find(n => n.id === openId && n.skill);

  const renderNode = (n: TraceNode) => {
    if (!n.skill) {
      return (
        <div key={n.id} data-node={n.id} className={`skill-trace-node skill-trace-outcome outcome-p${n.priority}`}>
          Priority {n.label.replace('P', '')}
        </div>
      );
    }
    const isCurrent = n.id === currentId;
    return (
      <button
        key={n.id}
        type="button"
        data-node={n.id}
        className={`skill-trace-node${isCurrent ? ' current' : ''}`}
        aria-current={isCurrent || undefined}
        aria-expanded={isCurrent ? openId === n.id : undefined}
        onClick={() => (isCurrent ? setOpenId(openId === n.id ? null : n.id) : onFollow(n.skill!))}
        title={isCurrent ? 'Show the rule text' : [n.note, 'Follow this skill'].filter(Boolean).join(' · ')}
      >
        <span className="skill-trace-node-name">{n.label}</span>
        {isCurrent && n.note && <span className="skill-trace-node-note">{n.note}</span>}
      </button>
    );
  };

  return (
    <section
      ref={sectionRef}
      className="skill-trace"
      aria-label={`How ${currentName} relates to other skills`}
      data-testid="skill-trace"
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="skill-trace-head">
        <div className="skill-trace-titles">
          <nav className="skill-trace-chain" aria-label="Skills followed">
            <ol>
              <li>
                <span className="skill-trace-crumb root">{regionName}</span>
              </li>
              {chain.slice(0, -1).map((s, i) => {
                const name = skillDisplayName(s);
                return (
                  <li key={`${s.kind}-${s.id}`}>
                    <FeatherIcon icon="chevron-right" size={12} aria-hidden="true" className="skill-trace-sep" />
                    <button type="button" className="skill-trace-crumb" onClick={() => onBackTo(i)} title={`Back to ${name}`}>
                      {name}
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>
          <h3 className="skill-trace-title">{currentName}</h3>
          <p className="skill-trace-summary" aria-live="polite">
            {trace.role === 'precedence' ? 'Decides the final priority' : traceRoleLabel(trace.role)}
            {trace.outcome ? (
              <>
                {' '}· {trace.outcome.includes('/') ? 'can lead to' : 'leads to'} <b>{trace.outcome}</b>
              </>
            ) : (
              ' · weighed with the other triage rules'
            )}
          </p>
        </div>
        <button type="button" className="skill-trace-close" onClick={onClose} aria-label="Close the skill trace" title="Close (Esc)">
          <FeatherIcon icon="x" size={16} />
        </button>
      </div>

      <div
        className="skill-trace-flow"
        ref={flowRef}
        style={{
          ['--trace-cols' as string]: trace.columns
            .map(c => (c.title === 'Priority' ? 'max-content' : 'minmax(0, 1fr)'))
            .join(' '),
        }}
      >
        <svg className="skill-trace-links" aria-hidden="true" focusable="false">
          {curves.map(c => (
            <path key={c.key} d={c.d} className={c.active ? 'active' : undefined} />
          ))}
        </svg>
        {trace.columns.map((col, i) => (
          <React.Fragment key={col.title}>
            <h4 className="skill-trace-col-head" style={{ ['--col' as string]: i + 1 }}>
              {i > 0 && <FeatherIcon icon="arrow-down" size={12} aria-hidden="true" className="skill-trace-step" />}
              {col.title}
            </h4>
            <div className="skill-trace-col-nodes" style={{ ['--col' as string]: i + 1 }}>
              {col.nodes.map(renderNode)}
            </div>
          </React.Fragment>
        ))}
      </div>

      {openNode?.skill && (
        <div className="skill-trace-detail">
          <b>{skillDisplayName(openNode.skill)}</b>
          <p>{openNode.skill.description || 'No rule text recorded for this skill.'}</p>
        </div>
      )}

      <p className="skill-trace-hint">Select a related skill to follow it. Select the highlighted skill to read its rule.</p>

      {trace.alsoApplied.length > 0 && (
        <div className="skill-trace-also" data-testid="skill-trace-also">
          <span>Also applied, never changes the priority:</span>
          <ul>
            {trace.alsoApplied.map(a => (
              <li key={a.skill.id} className="skill-trace-chip" title={a.group}>
                {skillDisplayName(a.skill)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
